import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Card, Input, Button, Select, Switch, Tag, Steps, App as AntdApp,
  Upload, Spin, Result, Row, Col,
} from 'antd';
import {
  ThumbsUp, MessageCircle, Lightbulb, AlertTriangle,
  Stethoscope, Building, Receipt, FileText, MoreHorizontal, HeartHandshake,
  Send, Upload as UploadIcon, Heart, Mail, Phone,
} from 'lucide-react';
import {
  publicFeedbackService,
  type Sentiment, type PublicFeedbackTopic,
  SENTIMENT_LABELS, SENTIMENT_COLORS,
} from '@/services/feedbackService';

/**
 * V1.0.37: Trang Hộp thư lắng nghe public — bệnh nhân truy cập qua QR code.
 * KHÔNG cần đăng nhập.
 *
 * Flow 3 bước:
 *  1. Chọn chủ đề + viết nội dung + sentiment
 *  2. Người gửi (anonymous/ko)
 *  3. Gửi đi → confirm + cảm ơn
 */
export function FeedbackPublicPage() {
  const { message } = AntdApp.useApp();
  const [currentStep, setCurrentStep] = useState(0);

  // Form state
  const [topicId, setTopicId] = useState<string | undefined>();
  const [sentiment, setSentiment] = useState<Sentiment>('POSITIVE');
  const [content, setContent] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [senderName, setSenderName] = useState('');
  const [senderPhone, setSenderPhone] = useState('');
  const [departmentId, setDepartmentId] = useState<string | undefined>();
  const [attachments, setAttachments] = useState<File[]>([]);

  // Submission result
  const [submittedResult, setSubmittedResult] = useState<{ code: string; message: string } | null>(null);

  // Load topics + departments
  const { data: topics = [], isLoading: topicsLoading } = useQuery<PublicFeedbackTopic[]>({
    queryKey: ['public-feedback-topics'],
    queryFn: () => publicFeedbackService.listTopics(),
  });

  const { data: departments = [] } = useQuery({
    queryKey: ['public-feedback-departments'],
    queryFn: () => publicFeedbackService.listDepartments(),
  });

  // Submit mutation
  const submitMut = useMutation({
    mutationFn: async () => {
      const result = await publicFeedbackService.submit({
        topicId,
        sentiment,
        content: content.trim(),
        isAnonymous,
        senderName: isAnonymous ? undefined : senderName.trim() || undefined,
        senderPhone: isAnonymous ? undefined : senderPhone.trim() || undefined,
        departmentVisited: departmentId,
      });

      // Upload attachments parallel
      if (attachments.length > 0) {
        await Promise.all(
          attachments.map(f =>
            publicFeedbackService.uploadAttachment(result.id, f).catch(err => {
              // Không fail toàn bộ submission nếu attachment lỗi
              console.warn('Upload attachment failed:', err);
            })
          )
        );
      }
      return result;
    },
    onSuccess: (result) => {
      setSubmittedResult(result);
      setCurrentStep(2);
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message ?? 'Gửi không thành công, vui lòng thử lại.');
    },
  });

  const handleSubmit = () => {
    if (!content.trim()) {
      message.warning('Vui lòng nhập nội dung phản hồi.');
      setCurrentStep(0);
      return;
    }
    if (content.length > 1000) {
      message.warning('Nội dung tối đa 1000 ký tự.');
      return;
    }
    if (!isAnonymous && !senderName.trim()) {
      message.warning('Vui lòng nhập họ tên hoặc bật "Gửi ẩn danh".');
      return;
    }
    submitMut.mutate();
  };

  const handleReset = () => {
    setCurrentStep(0);
    setTopicId(undefined);
    setSentiment('POSITIVE');
    setContent('');
    setIsAnonymous(false);
    setSenderName('');
    setSenderPhone('');
    setDepartmentId(undefined);
    setAttachments([]);
    setSubmittedResult(null);
  };

  // Map emoji string trong topic.emoji → icon component
  const topicIcon = (emoji?: string | null) => {
    if (!emoji) return <MessageCircle size={20} />;
    return <span style={{ fontSize: 22 }}>{emoji}</span>;
  };

  // ============== UI ==============
  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f0f4ff 0%, #e8f1ff 100%)',
      padding: '24px 16px',
    }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{
            fontSize: 32, fontWeight: 700, color: '#1e293b',
            fontFamily: 'serif', marginBottom: 8,
            lineHeight: 1.3,
          }}>
            Hộp thư lắng nghe
          </div>
          <div style={{ fontSize: 16, color: '#1e6feb', fontFamily: 'serif', marginBottom: 12 }}>
            Chia sẻ để hoàn thiện hơn
          </div>
          <div style={{ fontSize: 13, color: '#5b6875', maxWidth: 600, margin: '0 auto' }}>
            Mọi tâm tình và góp ý của bạn là món quà quý giá giúp Bệnh viện Hưng Việt
            thấu hiểu và chăm sóc bạn tốt hơn mỗi ngày.
          </div>
        </div>

        {/* Steps */}
        <Card style={{ marginBottom: 20, borderRadius: 12 }} bodyStyle={{ padding: '20px 32px' }}>
          <Steps
            current={currentStep}
            items={[
              { title: 'Thông tin' },
              { title: 'Viết thông điệp' },
              { title: 'Gửi đi' },
            ]}
          />
        </Card>

        {/* Step 2: thành công */}
        {currentStep === 2 && submittedResult && (
          <Card style={{ borderRadius: 12 }}>
            <Result
              status="success"
              icon={<Heart size={64} style={{ color: '#1e6feb' }} />}
              title={<span style={{ fontFamily: 'serif' }}>Cảm ơn bạn đã chia sẻ!</span>}
              subTitle={
                <div>
                  <div style={{ marginBottom: 12 }}>{submittedResult.message}</div>
                  <div style={{ fontSize: 12, color: '#7d8a98' }}>
                    Mã phản hồi: <strong>{submittedResult.code}</strong>
                  </div>
                </div>
              }
              extra={
                <Button type="primary" size="large" onClick={handleReset}>
                  Gửi thông điệp khác
                </Button>
              }
            />
          </Card>
        )}

        {/* Step 0-1 form */}
        {currentStep < 2 && (
          <Row gutter={[20, 20]}>
            <Col xs={24} lg={15} xl={16}>
              <Card style={{ borderRadius: 12 }} bodyStyle={{ padding: 24 }}>
                {/* Chủ đề */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 12, color: '#1e293b' }}>
                    <Mail size={18} style={{ verticalAlign: -3, marginRight: 6, color: '#1e6feb' }} />
                    Bạn muốn chia sẻ về điều gì?
                  </div>
                  {topicsLoading ? (
                    <Spin />
                  ) : (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                      gap: 10,
                    }}>
                    {topics.map(t => {
                      const selected = topicId === t.id;
                      return (
                        <button
                          key={t.id}
                          onClick={() => setTopicId(t.id)}
                          style={{
                            padding: 16,
                            border: selected ? '2px solid #1e6feb' : '1px solid #e8edf2',
                            background: selected ? '#e8f1ff' : '#fff',
                            borderRadius: 10,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            textAlign: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 6,
                          }}
                        >
                          {topicIcon(t.emoji)}
                          <span style={{ fontSize: 12, fontWeight: 500, color: '#1e293b' }}>{t.name}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Loại phản hồi */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 12, color: '#1e293b' }}>
                  <Heart size={18} style={{ verticalAlign: -3, marginRight: 6, color: '#1e6feb' }} />
                  Nội dung tâm tình
                </div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                  {(['POSITIVE', 'SUGGESTION', 'IDEA', 'COMPLAINT'] as Sentiment[]).map(s => {
                    const icon = {
                      POSITIVE: <ThumbsUp size={13} />,
                      SUGGESTION: <MessageCircle size={13} />,
                      IDEA: <Lightbulb size={13} />,
                      COMPLAINT: <AlertTriangle size={13} />,
                    }[s];
                    const isSelected = sentiment === s;
                    return (
                      <button
                        key={s}
                        onClick={() => setSentiment(s)}
                        style={{
                          padding: '6px 14px',
                          border: `1.5px solid ${isSelected ? SENTIMENT_COLORS[s] : '#e8edf2'}`,
                          background: isSelected ? `${SENTIMENT_COLORS[s]}15` : '#fff',
                          color: isSelected ? SENTIMENT_COLORS[s] : '#5b6875',
                          borderRadius: 20,
                          cursor: 'pointer',
                          fontWeight: 500,
                          fontSize: 12,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 5,
                          transition: 'all 0.15s',
                        }}
                      >
                        {icon}
                        {SENTIMENT_LABELS[s]}
                      </button>
                    );
                  })}
                </div>
                <Input.TextArea
                  rows={6}
                  maxLength={1000}
                  showCount
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Hãy viết những lời nhắn nhủ, cảm nhận hoặc mong muốn của bạn tại đây..."
                  style={{ borderRadius: 10, resize: 'none' }}
                />
              </div>

              {/* Tài liệu đính kèm */}
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8, color: '#1e293b' }}>
                  Tài liệu đính kèm (tối đa 10MB/file)
                </div>
                <Upload
                  multiple
                  beforeUpload={(file) => {
                    if (file.size > 10 * 1024 * 1024) {
                      message.warning(`File "${file.name}" quá lớn (tối đa 10MB).`);
                      return Upload.LIST_IGNORE;
                    }
                    setAttachments(prev => [...prev, file]);
                    return false; // prevent auto upload — upload sau khi submit
                  }}
                  onRemove={(file) => {
                    setAttachments(prev => prev.filter(f => f.name !== file.name));
                  }}
                  fileList={attachments.map(f => ({
                    uid: f.name + f.size,
                    name: f.name,
                    status: 'done' as const,
                    size: f.size,
                  }))}
                >
                  <Button icon={<UploadIcon size={14} />}>Chọn file</Button>
                </Upload>
              </div>
              </Card>
            </Col>

            {/* Right side: người gửi */}
            <Col xs={24} lg={9} xl={8}>
            <Card style={{ borderRadius: 12, height: 'fit-content' }} bodyStyle={{ padding: 24 }}>
              <div style={{
                display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', marginBottom: 16,
              }}>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#1e293b' }}>
                  Người gửi
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, color: '#5b6875' }}>Gửi ẩn danh</span>
                  <Switch
                    size="small"
                    checked={isAnonymous}
                    onChange={setIsAnonymous}
                  />
                </div>
              </div>

              {!isAnonymous && (
                <>
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 13, color: '#1e293b', marginBottom: 6 }}>Họ và tên</div>
                    <Input
                      value={senderName}
                      onChange={(e) => setSenderName(e.target.value)}
                      placeholder="Nguyễn Văn A"
                      style={{ borderRadius: 8 }}
                    />
                  </div>
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 13, color: '#1e293b', marginBottom: 6 }}>Số điện thoại</div>
                    <Input
                      value={senderPhone}
                      onChange={(e) => setSenderPhone(e.target.value)}
                      placeholder="090 123 4567"
                      style={{ borderRadius: 8 }}
                    />
                  </div>
                </>
              )}

              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 13, color: '#1e293b', marginBottom: 6 }}>
                  Khoa / Phòng đã thăm khám
                </div>
                <Select
                  showSearch
                  allowClear
                  placeholder="Chọn khoa phòng..."
                  value={departmentId}
                  onChange={setDepartmentId}
                  optionFilterProp="label"
                  style={{ width: '100%' }}
                  options={departments.map(d => ({ value: d.id, label: d.name }))}
                />
              </div>

              <div style={{
                background: '#f0f4ff', padding: 12, borderRadius: 8,
                fontSize: 11, color: '#5b6875', marginBottom: 16,
              }}>
                Chúng tôi cam kết bảo mật thông tin và luôn trân trọng mọi lời nhắn gửi từ bạn.
              </div>

              <Button
                type="primary"
                size="large"
                block
                icon={<Send size={14} />}
                onClick={handleSubmit}
                loading={submitMut.isPending}
                style={{
                  background: '#1e6feb',
                  borderRadius: 8, fontWeight: 600,
                }}
              >
                Gửi thông điệp
              </Button>

              <div style={{
                textAlign: 'center', marginTop: 16,
                fontSize: 12, color: '#7d8a98',
              }}>
                Cần hỗ trợ?
                <div style={{ color: '#1e6feb', fontWeight: 600, marginTop: 4 }}>
                  Hotline: 1900 1234
                </div>
              </div>
            </Card>
            </Col>
          </Row>
        )}
      </div>
    </div>
  );
}

export default FeedbackPublicPage;
